import { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { ApiError } from '../../lib/api';
import type { ProjectDetail, ProjectStatus, ProjectHealth } from '../../lib/projectsApi';
import type { BudgetCurrency, UpdateKbProjectInput } from '../../lib/kbProjectsApi';
import { useClients } from '../../hooks/useClients';
import { useUpdateKbProject } from '../../hooks/useKbProject';
import { PROJECT_STATUSES, PROJECT_HEALTHS, statusLabel, healthLabel } from './ProjectBadges';
import { Select } from '../ui/Select';
import { DateField } from '../ui/DateField';

const CURRENCIES: BudgetCurrency[] = ['RON', 'EUR'];

/** Date | ISO string -> `YYYY-MM-DD` for `DateField`. */
function toDateInput(value: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

/**
 * Edit a curated set of project fields (CLE-54).
 *
 * The DB is markdown-first (CLE-37): saving drives `PATCH /api/kb/projects/:code`
 * which rewrites the README (body-preserving), re-derives the DB row and
 * refreshes the projects index. Only Name / Status / Health / Budget(+currency) /
 * dates / description are editable here; richer changes still go through the AI
 * assistant.
 */
export function EditProjectModal({
  project,
  onClose,
}: {
  project: ProjectDetail;
  onClose: () => void;
}) {
  const codeOrId = project.code ?? project.id;
  const mutation = useUpdateKbProject(codeOrId);

  const [name, setName] = useState(project.name);
  const [status, setStatus] = useState<ProjectStatus>(project.status);
  const [health, setHealth] = useState<ProjectHealth>(project.health);
  const [clientId, setClientId] = useState(project.clientId ?? '');
  const [description, setDescription] = useState(project.description ?? '');
  const [budget, setBudget] = useState(project.budget != null ? String(project.budget) : '');
  // Seed from the project's saved currency. Hardcoding 'RON' here meant the
  // dropdown always reopened on RON and, because the form submits whatever it
  // shows, silently rewrote an EUR budget back to RON on the next save.
  const [currency, setCurrency] = useState<BudgetCurrency>(project.budgetCurrency ?? 'RON');
  const [startDate, setStartDate] = useState(toDateInput(project.startDate));
  const [endDate, setEndDate] = useState(toDateInput(project.endDate));
  const [validationError, setValidationError] = useState<string | null>(null);

  const clientsQuery = useClients({ active: 'true', limit: 100 });
  // Keep the currently linked client selectable even if it is inactive (and
  // therefore missing from the active-clients list).
  const clientOptions = useMemo(() => {
    const clients = clientsQuery.data?.data ?? [];
    const options = clients.map((c) => ({ value: c.id, label: c.name }));
    if (project.client && !clients.some((c) => c.id === project.client!.id)) {
      options.unshift({ value: project.client.id, label: project.client.name });
    }
    return options;
  }, [clientsQuery.data, project.client]);

  // Close on Escape; lock background scroll while open.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  function validate(): string | null {
    if (name.trim() === '') return 'Numele proiectului este obligatoriu.';
    if (name.trim().length > 200) return 'Numele proiectului este prea lung (max. 200 caractere).';
    if (!status) return 'Statusul este obligatoriu.';
    if (budget.trim() !== '') {
      const n = Number(budget);
      if (Number.isNaN(n)) return 'Bugetul trebuie să fie un număr.';
      if (n < 0) return 'Bugetul nu poate fi negativ.';
    }
    if (startDate && endDate && startDate > endDate) {
      return 'Data de început trebuie să fie înainte sau egală cu data de final.';
    }
    return null;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const error = validate();
    if (error) {
      setValidationError(error);
      return;
    }
    setValidationError(null);

    const payload: UpdateKbProjectInput = {
      // Sent only on an actual rename: an unchanged name would still take the
      // backend's rename path (uniqueness check, code pinning, title rewrite).
      ...(name.trim() !== project.name ? { name: name.trim() } : {}),
      status,
      health,
      clientId: clientId || null,
      description: description.trim() === '' ? null : description.trim(),
      budget: budget.trim() === '' ? null : Number(budget),
      budgetCurrency: currency,
      startDate: startDate === '' ? null : startDate,
      endDate: endDate === '' ? null : endDate,
    };

    mutation.mutate(payload, { onSuccess: () => onClose() });
  }

  const saving = mutation.isPending;
  const serverError =
    mutation.error instanceof ApiError
      ? mutation.error.message
      : mutation.error
        ? 'Salvarea modificărilor a eșuat.'
        : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Editează proiectul"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Editează proiectul</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Închide"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 px-5 py-4">
          {/* Name */}
          <label className="block">
            <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Nume proiect <span className="text-red-500">*</span>
            </span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
              required
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </label>

          {/* Status + Health */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="block">
              <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Status <span className="text-red-500">*</span>
              </span>
              <Select
                label="Status"
                hideLabel
                value={status}
                onChange={(v) => setStatus(v as ProjectStatus)}
                options={PROJECT_STATUSES.map((s) => ({ value: s, label: statusLabel(s) }))}
                className="mt-1"
              />
            </div>

            <div className="block">
              <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Sănătate
              </span>
              <Select
                label="Sănătate"
                hideLabel
                value={health}
                onChange={(v) => setHealth(v as ProjectHealth)}
                options={PROJECT_HEALTHS.map((h) => ({ value: h, label: healthLabel(h) }))}
                className="mt-1"
              />
            </div>
          </div>

          {/* Client */}
          <div className="block">
            <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Client
            </span>
            <Select
              label="Client"
              hideLabel
              clearable
              searchable
              value={clientId}
              onChange={setClientId}
              placeholder={clientOptions.length === 0 ? 'Încă niciun client' : 'Fără client'}
              options={clientOptions}
              className="mt-1"
            />
          </div>

          {/* Budget + currency */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <label className="block sm:col-span-2">
              <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Buget alocat
              </span>
              <input
                type="number"
                min={0}
                step="0.01"
                value={budget}
                onChange={(e) => setBudget(e.target.value)}
                placeholder="0"
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </label>
            <div className="block">
              <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Monedă
              </span>
              <Select
                label="Monedă"
                hideLabel
                value={currency}
                onChange={(v) => setCurrency(v as BudgetCurrency)}
                options={CURRENCIES.map((c) => ({ value: c, label: c }))}
                className="mt-1"
              />
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Data de început
              </span>
              <DateField value={startDate} onChange={setStartDate} className="mt-1 w-full" />
            </label>
            <label className="block">
              <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Termen
              </span>
              <DateField value={endDate} onChange={setEndDate} className="mt-1 w-full" />
            </label>
          </div>

          {/* Description */}
          <label className="block">
            <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Descriere
            </span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </label>

          {(validationError || serverError) && (
            <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
              {validationError ?? serverError}
            </p>
          )}

          <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
            >
              Anulează
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-700 disabled:opacity-50"
            >
              {saving && (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              )}
              {saving ? 'Se salvează…' : 'Salvează modificările'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
